import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { useLang } from "@/i18n/LangContext";
import { useAuth } from "@/lib/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { useState } from "react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1760681555328-8d545f42fe89?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwZXVyb3BlYW4lMjBob21lJTIwaW50ZXJpb3IlMjBuYXR1cmFsJTIwbGlnaHR8ZW58MHx8fHwxNzgwNjk1NjU5fDA&ixlib=rb-4.1.0&q=85";
const LIFE_IMG =
  "https://images.pexels.com/photos/6873928/pexels-photo-6873928.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const READ_IMG =
  "https://images.unsplash.com/photo-1512917602760-97a8fb80dca5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHx3b21hbiUyMGhvbGRpbmclMjB0ZWElMjBjdXAlMjBlbGVnYW50fGVufDB8fHx8MTc4MDY5NTY2N3ww&ixlib=rb-4.1.0&q=85";
const ARCH_IMG =
  "https://images.pexels.com/photos/20350968/pexels-photo-20350968.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const DOMAINS = ["logistica", "casa", "profissional", "financas", "educacao"];

export default function Landing() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [checkoutError, setCheckoutError] = useState("");

  const startCheckout = async (plan, provider = "lemonsqueezy") => {
    if (!user || !user.id) {
      navigate(`/login?intent=checkout&plan=${plan}&provider=${provider}`);
      return;
    }
    setCheckoutLoading(`${plan}:${provider}`);
    setCheckoutError("");
    try {
      const endpoint =
        provider === "paypal"
          ? "/payments/paypal/subscribe"
          : "/payments/lemonsqueezy/checkout";
      const { data } = await api.post(endpoint, {
        plan,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      setCheckoutError(formatApiError(err.response?.data?.detail) || err.message);
      setCheckoutLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <Header variant="landing" />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-24 md:pb-32 grid md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-7 reveal">
            <p className="label-cap mb-10" data-testid="hero-eyebrow">{t("landing.eyebrow")}</p>
            <h1
              className="font-display text-[44px] md:text-[80px] lg:text-[96px] leading-[0.95] tracking-tight font-light"
              data-testid="hero-title"
            >
              {t("landing.heroTitle")}
              <br />
              <span className="font-display-italic">{t("landing.heroTitleEm")}</span>
            </h1>
            <p className="mt-10 text-xl md:text-2xl font-display-italic text-[var(--ink-soft)] max-w-2xl">
              {t("landing.heroSub")}
            </p>
            <p className="mt-8 max-w-xl text-[15px] leading-relaxed text-[var(--ink-soft)]">
              {t("landing.heroBody")}
            </p>
            <div className="mt-12 flex flex-wrap items-center gap-6">
              <Link to="/register" className="btn-primary" data-testid="hero-cta-begin">
                {t("landing.ctaBegin")}
              </Link>
              <Link to="/login" className="btn-ghost" data-testid="hero-cta-login">
                {t("landing.ctaLogin")} →
              </Link>
            </div>
          </div>

          <div className="md:col-span-5 reveal reveal-delay-2">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={HERO_IMG}
                alt="Salão europeu elegante com luz natural"
                className="w-full h-full object-cover kenburns"
              />
              <div className="absolute inset-0 bg-[var(--ink)]/10 mix-blend-multiply" />
              <div className="absolute bottom-6 left-6 right-6 bg-[var(--bg)] px-5 py-4">
                <p className="label-cap">{t("landing.atDispor")}</p>
                <p className="font-display text-lg mt-1">— Domingo, 21h</p>
              </div>
            </div>
          </div>
        </div>
        <hr className="editorial-rule" />
      </section>

      {/* MANIFESTO */}
      <section id="manifesto" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-3">
            <p className="label-cap">— Manifesto</p>
          </div>
          <div className="md:col-span-9">
            <h2 className="font-display text-4xl md:text-5xl leading-tight font-light tracking-tight">
              {t("landing.manifestoTitle")}
            </h2>
            <p className="mt-10 text-[17px] leading-relaxed text-[var(--ink-soft)] max-w-3xl">
              {t("landing.manifestoBody")}
            </p>
            <blockquote className="mt-12 pl-6 border-l border-[var(--oxblood)] max-w-3xl">
              <p className="font-display-italic text-2xl md:text-3xl leading-snug text-[var(--ink)]">
                {t("landing.manifestoQuote")}
              </p>
            </blockquote>
          </div>
        </div>
      </section>

      <hr className="editorial-rule" />

      {/* DOMAINS */}
      <section id="domains" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid md:grid-cols-12 gap-12 mb-16">
            <div className="md:col-span-3">
              <p className="label-cap">— Cinco verticais</p>
            </div>
            <div className="md:col-span-9">
              <h2 className="font-display text-4xl md:text-5xl leading-tight font-light tracking-tight">
                {t("landing.domainsTitle")}
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border)]">
            {DOMAINS.map((d, i) => (
              <article
                key={d}
                className="bg-[var(--bg)] p-10 lift"
                data-testid={`domain-card-${d}`}
              >
                <p className="label-cap text-[var(--oxblood)]">
                  {String(i + 1).padStart(2, "0")} ·{" "}
                  <span className="text-[var(--ink-soft)]">
                    {t(`landing.domains.${d}.title`)}
                  </span>
                </p>
                <h3 className="font-display text-3xl mt-6 font-light tracking-tight">
                  {t(`landing.domains.${d}.title`)}
                </h3>
                <p className="mt-6 text-[15px] leading-relaxed text-[var(--ink-soft)]">
                  {t(`landing.domains.${d}.body`)}
                </p>
              </article>
            ))}
            <article className="bg-[var(--ink)] text-[var(--bg)] p-10 flex flex-col justify-between">
              <p className="label-cap text-[var(--gold)]">— O sistema</p>
              <div>
                <h3 className="font-display text-3xl mt-6 font-light tracking-tight">
                  {t("landing.operatingTitle")}
                </h3>
                <p className="mt-6 text-[15px] leading-relaxed text-[var(--bg)]/80">
                  {t("landing.operatingBody")}
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <hr className="editorial-rule" />

      {/* MOMENTS — editorial dossier */}
      <section className="py-24 md:py-32 bg-[var(--surface)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid md:grid-cols-12 gap-12 mb-16 items-end">
            <div className="md:col-span-3">
              <p className="label-cap">— Três momentos</p>
            </div>
            <div className="md:col-span-9">
              <h2 className="font-display text-4xl md:text-5xl leading-tight font-light tracking-tight">
                {t("landing.momentsTitle")}
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-7 space-y-16">
              {["tue", "sun", "thu"].map((m) => (
                <div key={m} className="grid grid-cols-12 gap-6 pb-12 border-b border-[var(--border)] last:border-b-0">
                  <div className="col-span-3">
                    <p className="label-cap text-[var(--oxblood)]">{t(`landing.moments.${m}.when`)}</p>
                  </div>
                  <div className="col-span-9">
                    <h3 className="font-display text-2xl md:text-3xl font-light leading-snug">
                      {t(`landing.moments.${m}.title`)}
                    </h3>
                    <p className="mt-4 text-[15px] leading-relaxed text-[var(--ink-soft)] font-display-italic">
                      {t(`landing.moments.${m}.body`)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="md:col-span-5">
              <div className="sticky top-28 space-y-6">
                <div className="aspect-[4/5] overflow-hidden">
                  <img src={READ_IMG} alt="Chá ao fim da tarde" className="w-full h-full object-cover" />
                </div>
                <div className="px-1">
                  <p className="label-cap">— Recuperação</p>
                  <p className="font-display text-xl mt-2 leading-snug">
                    8–15 horas mensais que voltam para si.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr className="editorial-rule" />

      {/* PRICING */}
      <section id="pricing" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid md:grid-cols-12 gap-12 mb-16">
            <div className="md:col-span-3">
              <p className="label-cap">— Pricing</p>
            </div>
            <div className="md:col-span-9">
              <h2 className="font-display text-4xl md:text-5xl leading-tight font-light tracking-tight">
                {t("landing.pricingTitle")}
              </h2>
              <p
                className="mt-6 font-display-italic text-2xl md:text-3xl text-[var(--oxblood)] leading-tight"
                data-testid="pricing-promise"
              >
                {t("landing.pricingPromise")}
              </p>
              <p className="mt-8 text-[17px] leading-relaxed text-[var(--ink-soft)] max-w-3xl">
                {t("landing.pricingBody")}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-12 gap-px bg-[var(--border)] max-w-4xl mx-auto">
            <div className="md:col-span-12">
              <PricingCard
                tier={t("landing.planSingle")}
                tag={t("landing.planSingleTag")}
                price="29.99"
                features={t("landing.planFeatures.single")}
                trial={t("landing.planTrial")}
                accent={true}
                testId="plan-single"
                onChoose={(provider) => startCheckout("basic", provider)}
                loadingProvider={
                  checkoutLoading?.startsWith("basic:") ? checkoutLoading.split(":")[1] : null
                }
              />
            </div>
          </div>
          {checkoutError && (
            <p className="text-[var(--error)] text-sm mt-6" data-testid="checkout-error">
              {checkoutError}
            </p>
          )}
        </div>
      </section>

      <hr className="editorial-rule" />

      {/* WHAT IT'S NOT */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="label-cap">— Diferenciação</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight font-light tracking-tight mt-6">
              {t("landing.whatNotTitle")}
            </h2>
            <div className="mt-10 aspect-[4/5] overflow-hidden max-w-md">
              <img src={ARCH_IMG} alt="Arquitectura lisboeta" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-7">
            <ol className="space-y-px">
              {t("landing.whatNot").map((line, i) => (
                <li
                  key={line}
                  className="grid grid-cols-12 items-baseline py-8 border-b border-[var(--border)]"
                  data-testid={`whatnot-item-${i}`}
                >
                  <span className="col-span-2 label-cap text-[var(--oxblood)]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="col-span-10 font-display text-2xl md:text-3xl font-light leading-snug">
                    {line}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <hr className="editorial-rule" />

      {/* FINAL CTA */}
      <section className="py-32 bg-[var(--ink)] text-[var(--bg)]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-left">
          <p className="label-cap text-[var(--gold)]">— {t("landing.atDispor")}</p>
          <h2 className="font-display text-5xl md:text-7xl font-light leading-[0.95] tracking-tight mt-8">
            {t("landing.finalCtaTitle")}
          </h2>
          <p className="font-display-italic text-xl md:text-2xl mt-8 text-[var(--bg)]/80 max-w-2xl">
            {t("landing.finalCtaBody")}
          </p>
          <div className="mt-12 flex flex-wrap items-center gap-6">
            <Link
              to="/register"
              className="bg-[var(--bg)] text-[var(--ink)] px-8 py-4 text-[12px] tracking-monocle uppercase hover:bg-[var(--gold)] hover:text-[var(--ink)] transition-colors duration-400"
              data-testid="final-cta-button"
            >
              {t("landing.finalCtaButton")}
            </Link>
            <Link to="/login" className="label-cap text-[var(--bg)]/70 hover:text-[var(--bg)]" data-testid="final-cta-login">
              {t("landing.ctaLogin")} →
            </Link>
          </div>
        </div>
      </section>

      <footer className="py-12 bg-[var(--bg)] border-t border-[var(--border)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p className="font-display text-lg">Mordomo</p>
          <nav className="flex flex-wrap gap-6 text-sm" data-testid="landing-legal-nav">
            <Link to="/privacy" className="label-cap text-[var(--ink-soft)] hover:text-[var(--oxblood)]" data-testid="landing-link-privacy">
              {t("landing.privacy") || (t("app.lang") === "en" ? "Privacy" : "Privacidade")}
            </Link>
            <Link to="/terms" className="label-cap text-[var(--ink-soft)] hover:text-[var(--oxblood)]" data-testid="landing-link-terms">
              {t("landing.terms") || (t("app.lang") === "en" ? "Terms" : "Termos")}
            </Link>
            <Link to="/data-deletion" className="label-cap text-[var(--ink-soft)] hover:text-[var(--oxblood)]" data-testid="landing-link-data-deletion">
              {t("app.lang") === "en" ? "Data deletion" : "Eliminação de dados"}
            </Link>
          </nav>
          <p className="label-cap text-[var(--ink-faint)]">{t("landing.footer")}</p>
        </div>
      </footer>
    </div>
  );
}

function PricingCard({ tier, tag, price, features, accent, testId, onChoose, loadingProvider, trial }) {
  const { t } = useLang();
  return (
    <div
      className={`p-10 flex flex-col ${accent ? "bg-[var(--oxblood)] text-[var(--bg)]" : "bg-[var(--bg)] text-[var(--ink)]"}`}
      data-testid={testId}
    >
      <p className={`label-cap ${accent ? "text-[var(--gold)]" : "text-[var(--oxblood)]"}`}>{tier}</p>
      {tag && (
        <p
          className={`mt-3 font-display-italic text-base leading-snug ${
            accent ? "text-[var(--bg)]/85" : "text-[var(--ink-soft)]"
          }`}
          data-testid={`${testId}-tag`}
        >
          {tag}
        </p>
      )}
      <p className="mt-8 flex items-baseline gap-2">
        <span className={`font-display text-6xl font-light tracking-tight`}>${price}</span>
        <span className={`text-[12px] tracking-monocle uppercase ${accent ? "text-[var(--bg)]/70" : "text-[var(--ink-soft)]"}`}>
          / {t("landing.planMonth")}
        </span>
      </p>
      {trial && (
        <p
          className={`mt-3 font-display-italic text-sm ${accent ? "text-[var(--gold)]" : "text-[var(--oxblood)]"}`}
          data-testid={`${testId}-trial`}
        >
          {trial}
        </p>
      )}
      <ul className="mt-8 space-y-4 flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-3 text-[15px] leading-relaxed">
            <span
              className={`mt-[0.45rem] inline-block w-3 h-px ${
                accent ? "bg-[var(--gold)]" : "bg-[var(--oxblood)]"
              }`}
            />
            <span className={accent ? "text-[var(--bg)]/95" : "text-[var(--ink)]"}>{f}</span>
          </li>
        ))}
      </ul>
      <div className="mt-10 grid grid-cols-1 gap-2">
        <button
          onClick={() => onChoose("lemonsqueezy")}
          disabled={loadingProvider !== null}
          data-testid={`${testId}-choose-btn`}
          className={`w-full py-4 text-[12px] tracking-monocle uppercase transition-colors duration-400 ${
            accent
              ? "bg-[var(--bg)] text-[var(--ink)] hover:bg-[var(--gold)]"
              : "bg-[var(--ink)] text-[var(--bg)] hover:bg-[var(--oxblood)]"
          } disabled:opacity-50`}
        >
          {loadingProvider === "lemonsqueezy" ? "…" : t("landing.payWithCard")}
        </button>
        <button
          onClick={() => onChoose("paypal")}
          disabled={loadingProvider !== null}
          data-testid={`${testId}-paypal-btn`}
          className={`w-full py-3 text-[12px] tracking-monocle uppercase border transition-colors duration-400 ${
            accent
              ? "border-[var(--bg)]/40 text-[var(--bg)] hover:border-[var(--gold)] hover:text-[var(--gold)]"
              : "border-[var(--ink)]/30 text-[var(--ink)] hover:border-[var(--oxblood)] hover:text-[var(--oxblood)]"
          } disabled:opacity-50`}
        >
          {loadingProvider === "paypal" ? "…" : t("landing.payWithPaypal")}
        </button>
      </div>
    </div>
  );
}
